// frontend/src/pages/LandingPage.js
import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button, Navbar, Nav } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';
import { Check, Zap, BarChart2, Droplet, Globe, ArrowRight } from 'react-feather';
import '../assets/css/LandingPage.css';

const LandingPage = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(user.hasOnboarded ? '/dashboard' : '/onboarding');
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="landing-page">
      {/* Navbar fijo */}
      <Navbar 
        fixed="top" 
        className={`landing-navbar ${scrolled ? 'scrolled' : ''}`}
        expand="lg"
      >
        <Container>
          <Navbar.Brand href="/" className="fw-bold fs-4">
            <span className="brand-gradient">LinksEasy</span>
          </Navbar.Brand>
              
          
            <Nav>
              <div className="d-flex gap-2 align-items-center">
                <GoogleLoginButton variant="outline" />
              </div>
            </Nav>
          
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <Container>
          <Row className="align-items-center min-vh-100 d-flex  align-content-center ">
            <Col lg={6} className="text-center text-lg-start order-2 order-lg-1">
              <h1 className="display-3 fw-bold mb-4">
                Tu centro de enlaces{' '}
                <span className="gradient-text">personalizado</span>
              </h1>
              <p className="lead text-muted mb-4">
                Comparte todos tus perfiles en un solo lugar. 
                Personaliza colores, íconos y obtén estadísticas de tus clics.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start">
                <GoogleLoginButton variant="primary" text="Comenzar gratis" />
                <Button 
                  variant="outline-secondary" 
                  onClick={() => scrollToSection('features')}
                >
                  Saber más
                  <ArrowRight size={16} className="ms-2" />
                </Button>
              </div>
            </Col>
            <Col lg={6} className="d-block order-1 order-lg-2">
              <div className="hero-illustration">
                <div className="floating-card card-1">
                  <Globe size={24} />
                  <span>Perfil público</span>
                </div>
                <div className="floating-card card-2">
                  <Droplet size={24} />
                  <span>Personaliza colores</span>
                </div>
                <div className="floating-card card-3">
                  <BarChart2 size={24} />
                  <span>Estadísticas de tus links</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section bg-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">
              Todo lo que necesitas en el plan <span className="gradient-text">gratuito</span>
            </h2>
            <p className="lead text-muted">
              Comienza gratis y actualiza cuando quieras
            </p>
          </div>
          <Row className="g-4">
            <Col md={6} lg={3}>
              <div className="feature-card text-center">
                <div className="feature-icon">
                  <Check size={32} />
                </div>
                <h5>5 enlaces</h5>
                <p className="text-muted">Máximo 5 enlaces para empezar</p>
              </div>
            </Col>
            <Col md={6} lg={3}>
              <div className="feature-card text-center">
                <div className="feature-icon">
                  <Droplet size={32} />
                </div>
                <h5>Personalizable</h5>
                <p className="text-muted">Colores, íconos y estilos a tu gusto</p>
              </div>
            </Col>
            <Col md={6} lg={3}>
              <div className="feature-card text-center">
                <div className="feature-icon">
                  <BarChart2 size={32} />
                </div>
                <h5>Estadísticas</h5>
                <p className="text-muted">Seguimiento de clics en tiempo real</p>
              </div>
            </Col>
            <Col md={6} lg={3}>
              <div className="feature-card text-center">
                <div className="feature-icon">
                  <Globe size={32} />
                </div>
                <h5>Perfil público</h5>
                <p className="text-muted">Tu landing page personalizada</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container className="text-center">
          <h2 className="display-5 fw-bold mb-4">
            ¿Listo para <span className="gradient-text">comenzar</span>?
          </h2>
          <p className="lead mb-4">
            Únete a miles de usuarios que ya gestionan sus enlaces con LinksEasy
          </p>
          <GoogleLoginButton variant="primary" text="Regístrate gratis ahora" />
        </Container>
      </section>

      {/* Footer */}
      <footer className="footer bg-dark text-white">
        <Container className=''>
          <Row className="py-5 text-center justify-content-center  ">
            <Col md={4} className="mb-4 mb-md-0">
              <h5 className="fw-bold mb-3">LinksEasy</h5>
              <p className="text-white">
                Tu centro de enlaces personalizado. 
                Comparte todos tus perfiles en un solo lugar.
              </p>
            </Col>
            {/* <Col md={2} className="mb-4 mb-md-0">
              <h6>Producto</h6>
              <ul className="list-unstyled">
                <li><a href="#features">Características</a></li>
                <li><a href="#pricing">Precios</a></li>
              </ul>
            </Col>
            <Col md={2} className="mb-4 mb-md-0">
              <h6>Legal</h6>
              <ul className="list-unstyled">
                <li><a href="/terms">Términos</a></li>
                <li><a href="/privacy">Privacidad</a></li>
              </ul>
            </Col> */}
            {/* <Col md={4}>
              <h6>Síguenos</h6>
              <div className="d-flex gap-3">
                <a href="#" className="social-link">Instagram</a>
                <a href="#" className="social-link">Twitter</a>
                <a href="#" className="social-link">GitHub</a>
              </div>
            </Col> */}
          </Row>
          <hr className="bg-secondary" />
          <div className="text-center py-3 text-white">
            <small>&copy; 2026 LinksEasy. Todos los derechos reservados.</small>
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default LandingPage;